<template>
  <div class="process-row group relative flex items-center gap-3 px-3.5 py-2.5 border-b border-slate-100 last:border-b-0">
    <!-- icono tipo fichero -->
    <div class="flex-none w-8 h-8 rounded-md grid place-items-center bg-slate-100 text-slate-500">
      <b-icon icon="file-alt" pack="fas" size="is-small"></b-icon>
    </div>

    <!-- nombre + estado -->
    <div class="flex-1 min-w-0">
      <p class="truncate text-sm font-semibold text-slate-700" :title="data.description">
        {{ data.description }}
      </p>
      <p class="text-xs mt-0.5 flex items-center gap-1.5" :class="isError ? 'text-red' : 'text-slate-500'">
        <span>{{ statusLabel }}</span>
        <span v-if="inFlight" class="tabular-nums">· {{ Math.round(data.progress) }}%</span>
      </p>
    </div>

    <!-- estado a la derecha; en hover el botón eliminar ocupa esta misma celda (centrado) -->
    <div class="flex-none w-6 h-6 grid place-items-center">
      <!-- indicador de estado -->
      <span class="status-indicator grid place-items-center">
        <!-- en curso: avance fijo en azul + segmento azul claro girando -->
        <svg v-if="inFlight" class="process-ring" width="24" height="24" viewBox="0 0 24 24">
          <circle class="ring-track" cx="12" cy="12" r="9"></circle>
          <circle
            class="ring-bar"
            cx="12" cy="12" r="9"
            :stroke-dasharray="ringCircumference"
            :stroke-dashoffset="ringOffset"></circle>
          <circle class="ring-spin" cx="12" cy="12" r="9"></circle>
        </svg>
        <!-- completado -->
        <span v-else-if="data.status == 2" class="w-5 h-5 rounded-full grid place-items-center bg-green text-white">
          <b-icon icon="check" pack="fas" custom-size="fa-xs"></b-icon>
        </span>
        <!-- error -->
        <span v-else-if="isError" class="w-5 h-5 rounded-full grid place-items-center bg-red text-white">
          <b-icon icon="times" pack="fas" custom-size="fa-xs"></b-icon>
        </span>
      </span>

      <!-- eliminar (solo procesos en cola / en curso) -->
      <button
        v-if="data.status === 0 || data.status === 1"
        type="button"
        class="process-del w-6 h-6 rounded-md grid place-items-center bg-white text-red"
        :title="$t('delete')"
        @click="onDelete(data.id)">
        <b-icon icon="trash-alt" pack="fas" size="is-small"></b-icon>
      </button>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import axios from 'axios'

export default {
  components: {
  },

  props: {
    data: { type: Object, required: true }
  },

  computed: {
    ...mapGetters({
      locale: 'lang/locale',
    }),

    // en cola (0) o procesando (1)
    inFlight() {
      return this.data.status === 0 || this.data.status === 1
    },

    // tratamos cualquier estado >= 3 como error (backend marca 3; antes el icono miraba 4)
    isError() {
      return this.data.status >= 3
    },

    // description_status puede venir como clave i18n (ej: report_generating) o como texto plano
    // (procesos antiguos / otros jobs). Traducimos si existe la clave; si no, mostramos el texto tal cual.
    statusLabel() {
      const value = this.data.description_status
      if (!value) return ''
      return this.$te(value) ? this.$t(value) : value
    },

    ringCircumference() {
      return (2 * Math.PI * 9).toFixed(1)
    },

    ringOffset() {
      const progress = Math.max(0, Math.min(100, this.data.progress || 0))
      return (2 * Math.PI * 9 * (1 - progress / 100)).toFixed(1)
    },
  },

  methods: {
    formatDate(datetime) {
      return moment(new Date(datetime), 'YYYY-MM-DD HH:mm:ss')
    },

    async onDelete(id) {
      try {
        const response = await axios.delete(`/api/v2/process-logs/${id}/delete`)
        if (response.data && response.data.success) {
          this.$emit('processlog-deleted', id)
        } else {
          this.$buefy.toast.open({
            message: this.$t('error_deleting'),
            type: 'is-danger'
          })
        }
      } catch (e) {
        this.$buefy.toast.open({
          message: this.$t('error_deleting'),
          type: 'is-danger'
        })
      }
    }
  },
};
</script>

<style scoped>
/* estado y botón eliminar comparten la misma celda (superpuestos y centrados) */
.status-indicator,
.process-del {
  grid-area: 1 / 1;
}
/* en hover el botón eliminar sustituye al icono de estado, sin descolocarlo */
.process-del { display: none; }
.process-row:hover .process-del { display: grid; }
.process-row:hover .status-indicator { visibility: hidden; }

.process-ring .ring-track { fill: none; stroke: #e7ebf1; stroke-width: 3; }

/* arco de avance fijo (empieza arriba, muestra el %) */
.process-ring .ring-bar {
  fill: none;
  stroke: #2f6fed;
  stroke-width: 3;
  stroke-linecap: round;
  transform: rotate(-90deg);
  transform-origin: center;
  transition: stroke-dashoffset 0.4s ease;
}

/* segmento claro que gira encima para dar sensación de actividad */
.process-ring .ring-spin {
  fill: none;
  stroke: #9ec1fb;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: 14 60;
  transform-origin: center;
  animation: ring-spin 0.9s linear infinite;
}
@keyframes ring-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .process-ring .ring-spin { animation-duration: 3s; }
}
</style>
